import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BulkImportModal from '../components/BulkImportModal';
import { UserCheck, Search, Plus, FileText, CheckCircle, AlertCircle, Trash2, Edit3, Sparkles, Phone, Mail, Building2, X, FileSpreadsheet } from 'lucide-react';

export default function ApplicantManagement() {
    const [applicants, setApplicants] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [pageError, setPageError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const [selectedSlip, setSelectedSlip] = useState(null);

    const [showImportModal, setShowImportModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assignTargetId, setAssignTargetId] = useState(null);
    const [assignedCompanies, setAssignedCompanies] = useState([]);
    const [assignError, setAssignError] = useState('');
    const [submittingAssign, setSubmittingAssign] = useState(false);

    const fetchData = async () => {
        setLoading(true);
        setPageError('');
        try {
            const [aRes, cRes] = await Promise.all([
                axios.get('/api/applicants'),
                axios.get('/api/companies')
            ]);
            if (aRes.data.success) setApplicants(aRes.data.applicants);
            if (cRes.data.success) setCompanies(cRes.data.companies);
        } catch (err) {
            setPageError(err.response?.data?.message || 'Failed to fetch applicants.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const openAssignModal = (applicant) => {
        setAssignTargetId(applicant.id);
        const existing = applicant.company_visits?.map(v => v.company_id) || [];
        setAssignedCompanies(existing);
        setAssignError('');
        setShowAssignModal(true);
    };

    const handleAssignSubmit = async (e) => {
        e.preventDefault();
        setAssignError('');
        setSubmittingAssign(true);
        try {
            const payload = {
                applicant_id: assignTargetId,
                companies: assignedCompanies.map((cId, idx) => ({
                    company_id: cId,
                    visit_order: idx + 1,
                }))
            };
            const res = await axios.post('/api/applicant-companies/assign', payload);
            if (res.data.success) {
                setSuccessMsg('Stalls assigned successfully!');
                setShowAssignModal(false);
                fetchData();
            }
        } catch (err) {
            setAssignError(err.response?.data?.message || 'Failed to assign companies.');
        } finally {
            setSubmittingAssign(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this applicant?')) return;
        try {
            const res = await axios.delete(`/api/applicants/${id}`);
            if (res.data.success) {
                setSuccessMsg('Applicant deleted.');
                fetchData();
            }
        } catch (err) {
            setPageError(err.response?.data?.message || 'Delete failed.');
        }
    };

    const filteredApplicants = applicants.filter(a => {
        const term = searchTerm.toLowerCase();
        return (
            a.name.toLowerCase().includes(term) ||
            a.phone_number.includes(term) ||
            a.email.toLowerCase().includes(term) ||
            (a.job_applying_for && a.job_applying_for.toLowerCase().includes(term))
        );
    });

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
            
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20">
                        Candidate Portal
                    </span>
                    <h1 className="text-3xl font-extrabold mt-2 bg-gradient-to-r from-white via-slate-100 to-emerald-300 bg-clip-text text-transparent">
                        Job Applicant Registry
                    </h1>
                    <p className="text-sm text-slate-400">All registered job seekers, preferred sectors & stall visit queues</p>
                </div>

                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => setShowImportModal(true)}
                        className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-emerald-400 font-bold text-xs border border-slate-700 shadow-md flex items-center space-x-2 transition-all"
                    >
                        <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                        <span>Bulk Import Excel</span>
                    </button>

                    <div className="relative">
                        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Search mobile, name, sector..."
                            className="pl-9 pr-4 py-2 rounded-xl glass-input text-xs w-64"
                        />
                    </div>
                </div>
            </div>

            {/* Notifications */}
            {successMsg && (
                <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-sm flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                        <span>{successMsg}</span>
                    </div>
                    <button onClick={() => setSuccessMsg('')} className="text-xs text-slate-400 hover:text-white font-bold">Dismiss</button>
                </div>
            )}

            {pageError && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-300 text-sm flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
                        <span>{pageError}</span>
                    </div>
                    <button onClick={() => setPageError('')} className="text-xs text-slate-400 hover:text-white font-bold">Dismiss</button>
                </div>
            )}

            {/* Applicants Table */}
            <div className="glass-card rounded-2xl overflow-hidden border border-slate-800 shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-slate-300">
                        <thead className="bg-slate-900/90 text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-800">
                            <tr>
                                <th className="p-4">Applicant Name</th>
                                <th className="p-4">Mobile & Email</th>
                                <th className="p-4">Preferred Sectors</th>
                                <th className="p-4">Assigned Stalls (Order 1, 2, 3)</th>
                                <th className="p-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/60">
                            {filteredApplicants.map(a => (
                                <tr key={a.id} className="hover:bg-slate-900/50 transition-colors">
                                    <td className="p-4">
                                        <div className="font-bold text-white text-base">{a.name}</div>
                                        <div className="text-xs text-slate-400">🎓 {a.highest_education || 'Graduate'} • 📍 {a.city || 'N/A'}</div>
                                    </td>

                                    <td className="p-4 text-xs">
                                        <div className="font-semibold text-slate-200">📱 {a.phone_number}</div>
                                        <div className="text-slate-400">✉️ {a.email}</div>
                                    </td>

                                    <td className="p-4">
                                        <div className="flex flex-wrap gap-1">
                                            {a.job_applying_for ? a.job_applying_for.split(',').map((sec, idx) => (
                                                <span key={idx} className="text-[10px] font-semibold bg-indigo-950 text-indigo-300 border border-indigo-500/30 px-2 py-0.5 rounded">
                                                    {sec.trim()}
                                                </span>
                                            )) : <span className="text-xs text-slate-500">General</span>}
                                        </div>
                                    </td>

                                    <td className="p-4 text-xs">
                                        <div className="flex items-center space-x-1.5 flex-wrap gap-1">
                                            {a.company_visits?.map((v, i) => (
                                                <span key={v.id} className={`text-[10px] font-bold px-2 py-0.5 rounded border ${
                                                    v.status === 'selected' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' :
                                                    v.status === 'rejected' ? 'bg-rose-500/20 text-rose-300 border-rose-500/30' :
                                                    'bg-slate-900 text-amber-300 border-slate-800'
                                                }`}>
                                                    #{v.visit_order || (i+1)} {v.company?.company_name} ({v.status})
                                                </span>
                                            ))}
                                        </div>
                                    </td>

                                    <td className="p-4 text-right space-x-2">
                                        <button
                                            onClick={() => setSelectedSlip(a)}
                                            className="px-3 py-1.5 rounded-lg bg-indigo-600/30 text-indigo-300 hover:bg-indigo-600/50 border border-indigo-500/30 text-xs font-semibold"
                                            title="View Job Fair Slip"
                                        >
                                            <FileText className="w-4 h-4 inline mr-1" />
                                            <span>Slip</span>
                                        </button>

                                        <button
                                            onClick={() => openAssignModal(a)}
                                            className="px-3 py-1.5 rounded-lg bg-purple-600/30 text-purple-300 hover:bg-purple-600/50 border border-purple-500/30 text-xs font-semibold"
                                            title="Assign Companies"
                                        >
                                            <Building2 className="w-4 h-4 inline mr-1" />
                                            <span>Assign Stalls</span>
                                        </button>

                                        <button
                                            onClick={() => handleDelete(a.id)}
                                            className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View Printable Slip Modal */}
            {selectedSlip && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md">
                    <div className="w-full max-w-lg glass-card rounded-2xl p-6 border border-slate-800 shadow-2xl relative">
                        <div className="text-center border-b border-slate-800 pb-4 mb-4">
                            <span className="text-xs font-bold text-indigo-400 tracking-widest uppercase">Job Fair Pass & Queue Slip</span>
                            <h2 className="text-2xl font-black text-white mt-1">{selectedSlip.name}</h2>
                            <div className="text-xs text-slate-400">Phone: {selectedSlip.phone_number} | Reg ID: #{selectedSlip.id}</div>
                        </div>

                        <div className="space-y-3 text-xs mb-6">
                            <div className="flex justify-between">
                                <span className="text-slate-400">Highest Education:</span>
                                <span className="font-bold text-white">{selectedSlip.highest_education || 'N/A'}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-400">Experience Type:</span>
                                <span className="font-bold text-amber-300">{selectedSlip.experience_type || 'Fresher'} ({selectedSlip.years_of_experience || '0 Yrs'})</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-slate-400">Preferred Sectors:</span>
                                <span className="font-bold text-purple-300">{selectedSlip.job_applying_for || 'General'}</span>
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="text-xs font-bold text-amber-400 uppercase tracking-wider mb-2 flex items-center space-x-1">
                                <Sparkles className="w-4 h-4" />
                                <span>Stall Visit Sequence (Order 1, 2, 3)</span>
                            </div>

                            <div className="space-y-2">
                                {selectedSlip.company_visits?.map((v, i) => (
                                    <div key={v.id} className="p-3 bg-slate-900 rounded-xl border border-slate-800 flex items-center justify-between">
                                        <div>
                                            <span className="text-[10px] font-extrabold text-indigo-400 bg-indigo-500/20 px-2 py-0.5 rounded">
                                                STALL #{v.visit_order || (i+1)}
                                            </span>
                                            <div className="font-bold text-sm text-white mt-1">{v.company?.company_name}</div>
                                            <div className="text-[10px] text-slate-400">📍 {v.company?.office_location}</div>
                                        </div>

                                        <span className={`px-2.5 py-1 rounded text-xs font-extrabold uppercase ${
                                            v.status === 'selected' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' :
                                            v.status === 'rejected' ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30' :
                                            'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                        }`}>
                                            {v.status}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
                            <button
                                onClick={() => setSelectedSlip(null)}
                                className="px-5 py-2 rounded-xl bg-slate-800 text-white font-bold text-xs"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => window.print()}
                                className="px-5 py-2 rounded-xl bg-indigo-600 text-white font-bold text-xs shadow-lg shadow-indigo-600/30"
                            >
                                Print Slip
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Companies Modal */}
            {showAssignModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-fade-in">
                    <div className="w-full max-w-md glass-card rounded-3xl p-6 border border-slate-800 shadow-2xl relative">
                        <div className="flex items-center justify-between mb-3 border-b border-slate-800 pb-3">
                            <div>
                                <h2 className="text-xl font-bold text-white">Assign Company Stalls</h2>
                                <p className="text-xs text-slate-400">Select companies to add to candidate's visit queue (Order 1, 2, 3)</p>
                            </div>
                            <button onClick={() => setShowAssignModal(false)} className="text-slate-400 hover:text-white">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {assignError && (
                            <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold flex items-center space-x-2">
                                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                                <span>{assignError}</span>
                            </div>
                        )}

                        <form onSubmit={handleAssignSubmit} className="space-y-4">
                            <div className="max-h-60 overflow-y-auto space-y-2 pr-1">
                                {companies.map(c => {
                                    const isSel = assignedCompanies.includes(c.id);
                                    return (
                                        <div
                                            key={c.id}
                                            onClick={() => {
                                                if (isSel) setAssignedCompanies(assignedCompanies.filter(id => id !== c.id));
                                                else setAssignedCompanies([...assignedCompanies, c.id]);
                                            }}
                                            className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                                                isSel ? 'bg-indigo-950/80 border-indigo-500 text-white' : 'bg-slate-900 border-slate-800 text-slate-400'
                                            }`}
                                        >
                                            <div>
                                                <div className="font-bold text-xs">{c.company_name}</div>
                                                <div className="text-[10px] text-slate-500">{c.sector}</div>
                                            </div>
                                            {isSel && <span className="text-xs font-bold text-indigo-400">Selected ✓</span>}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowAssignModal(false)}
                                    className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 text-xs font-semibold"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={submittingAssign}
                                    className="px-5 py-2 rounded-xl bg-purple-600 text-white text-xs font-bold shadow-lg shadow-purple-600/30"
                                >
                                    {submittingAssign ? 'Saving...' : 'Save Stall Queue'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Bulk Import Modal */}
            <BulkImportModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                type="applicants"
                onSuccess={fetchData}
            />
        </div>
    );
}
